<template>
  <div class="slider-wrapper" :style="cssVars">
    <div class="slider-track" :class="{ 'is-centered': items.length < 4 }">
      <div
        v-for="(item, index) in items"
        :key="`placeholder-${index}`"
        class="slider-item"
      >
        <slot :item="item" />
      </div>
    </div>
    <div v-if="$scopedSlots['overlay'] || $slots['overlay']" class="slider-overlay">
      <slot name="overlay" />
    </div>
  </div>
</template>
<script>

export default {
  name: 'Slider',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    gap: {
      type: Number,
      default: 0
    },
    visibleItems: {
      type: [Object, Number],
      default: 1
    },
    coverflow: {
      type: [Object, Boolean],
      default: false
    },
    coverflowCentered: {
      type: Boolean,
      default: false
    },
    itemRatio: {
      type: String,
      default: null
    }
  },
  computed: {
    visibleItemsByViewport () {
      return this.getResponsiveValues(this.visibleItems, 1, 'number')
    },
    coverflowByViewport () {
      return this.getResponsiveValues(this.coverflow, false, 'boolean')
    },
    effectiveVisibleItems () {
      const values = { ...this.visibleItemsByViewport }
      if (this.coverflowByViewport.mobile || this.coverflowByViewport.tablet || this.coverflowCentered) {
        values.mobile = 1
        values.tablet = 1
        values.desktop = typeof this.visibleItems === 'number' && this.visibleItems === 1 ? 1 : 3
      }
      return values
    },
    cssVars () {
      const column = (visibleItems, coverflow) => {
        if (coverflow) {
          return `calc((100% - ${2 * this.gap}px) / 1.2)`
        }
        return visibleItems > 1
          ? `calc((100% - ${(visibleItems - 1) * this.gap}px) / ${visibleItems})`
          : '100%'
      }
      const scale = coverflow => (this.coverflowCentered && coverflow ? '0.9' : '1')

      return {
        '--slider-gap': `${this.gap}px`,
        '--slider-scale-mobile': scale(this.coverflowByViewport.mobile),
        '--slider-scale-tablet': scale(this.coverflowByViewport.tablet),
        '--slider-scale-desktop': scale(this.coverflowByViewport.desktop),
        '--slider-scale-fullhd': scale(this.coverflowByViewport.fullhd),
        '--slider-item-ratio': this.itemRatio ? this.itemRatio.replace('by', ' / ') : 'auto',
        '--slider-col-mobile': column(this.effectiveVisibleItems.mobile, this.coverflowByViewport.mobile),
        '--slider-col-tablet': column(this.effectiveVisibleItems.tablet, this.coverflowByViewport.tablet),
        '--slider-col-desktop': column(this.effectiveVisibleItems.desktop, this.coverflowByViewport.desktop),
        '--slider-col-fullhd': column(this.effectiveVisibleItems.fullhd, this.coverflowByViewport.fullhd)
      }
    }
  },
  methods: {
    getResponsiveValues (value, fallback, type) {
      const isExpectedType = type === 'number'
        ? item => typeof item === 'number'
        : item => typeof item === 'boolean'
      if (isExpectedType(value)) {
        return {
          mobile: value,
          tablet: value,
          desktop: value,
          fullhd: value
        }
      }

      const responsiveValue = value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : {}
      const mobile = isExpectedType(responsiveValue.mobile) ? responsiveValue.mobile : fallback
      const tablet = isExpectedType(responsiveValue.tablet) ? responsiveValue.tablet : mobile
      const desktop = isExpectedType(responsiveValue.desktop) ? responsiveValue.desktop : tablet
      const fullhd = isExpectedType(responsiveValue.fullhd) ? responsiveValue.fullhd : desktop

      return { mobile, tablet, desktop, fullhd }
    }
  }
}

</script>
<style lang="scss" scoped>

.slider-wrapper {
  --slider-col: var(--slider-col-fullhd);
  --slider-scale: var(--slider-scale-fullhd);

  position: relative;
  width: 100%;

  @media screen and (max-width: 1407px) {
    --slider-col: var(--slider-col-desktop);
    --slider-scale: var(--slider-scale-desktop);
  }

  @media screen and (max-width: 1023px) {
    --slider-col: var(--slider-col-tablet);
    --slider-scale: var(--slider-scale-tablet);
  }

  @media screen and (max-width: 767px) {
    --slider-col: var(--slider-col-mobile);
    --slider-scale: var(--slider-scale-mobile);
  }
}

.slider-item {
  position: relative;
  aspect-ratio: var(--slider-item-ratio, auto);
  scale: var(--slider-scale, 1);
  :deep(.category-features svg) {
    width: 30px;
    height: 30px;
  }
}

.slider-track {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--slider-col);

  gap: var(--slider-gap);
  width: 100%;

  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;

  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: thin;

  -webkit-overflow-scrolling: touch;
}

.slider-track.is-centered {
  justify-content: safe center;
}

.slider-track > * {
  min-width: 0;
  height: auto;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.slider-track::-webkit-scrollbar {
  height: 6px;
}

.slider-track::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 999px;
}

.slider-track::-webkit-scrollbar-thumb {
  background: #b5b5b5;
  border-radius: 999px;
}

@media screen and (max-width: 767px) {
  .slider-track > * {
    scroll-snap-align: center;
  }
}

.slider-overlay {
  position: absolute;
  bottom: calc(3.5% + 7px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  font-size: 14px;
}

</style>
